import { memo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import useSWR from "swr";

import Breadcrumbs from "@mui/material/Breadcrumbs";
import Breadcrumb from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import CompletedPhase from "../../aws/Connect/Phase/Completed";

import { useConnectionStore } from "stores/ConnectionStore";
import { useFlairStore } from "stores/FlairStore";
import {
  ActionState,
  ProvisionState,
  SecretState,
  swrFetcher,
} from "stores/lib";

import { captureSentryException } from "utility/capture_sentry_exception";

import LoaderModal from "layouts/components/LoaderModal";

import ServiceConnectionInstructions from "components/ServiceConnectionInstructions";

import Error from "./Error";
import ConfiguringOktaPhase from "./Phase/ConfiguringOkta";
import RecreatingApplicationPhase from "./Phase/RecreatingApplication";
import VerifyingAPITokenPhase from "./Phase/VerifyingAPIToken";
import VerifyingIntegrationPhase from "./Phase/VerifyingIntegration";

// -----------------------------------------------------------------------------

enum Phase {
  EnterDetails,
  Provisioning,
  Done,
  Error,
}

enum ProvisionPhase {
  VerifyingAPIToken,
  RecreatingApplication,
  VerifyingIntegration,
  Done,
}

const ProvisionURL = "api/organization/okta/provision";

// -----------------------------------------------------------------------------

const OktaConnectSetupPage = memo(function OktaConnectSetupPage() {
  const [connectionStore, connectionActions] = useConnectionStore();
  const [, flairActions] = useFlairStore();

  const [phase, setPhase] = useState(Phase.EnterDetails);
  const [provisionPhase, setProvisionPhase] = useState(
    ProvisionPhase.VerifyingAPIToken
  );
  const [refreshInterval, setRefreshInterval] = useState(2000);
  const [errorMessage, setErrorMessage] = useState("");

  const [organization, setOrganization] = useState("");
  const [apiToken, setAPIToken] = useState("");
  const [mode, setMode] = useState<"readwrite" | "readonly">("readwrite");

  //
  useSWR<ProvisionStateResponse>(
    ProvisionURL,
    phase === Phase.Provisioning
      ? async (url) =>
          await swrFetcher(url, {
            method: "POST",
            body: JSON.stringify({
              org_url: organization,
              ro: mode === "readonly",
            }),
          })
      : null,
    {
      refreshInterval,
      onError: (error) => {
        captureSentryException(error, "Failed to validate Okta provisioning");
        setRefreshInterval(0);
        setPhase(Phase.Error);

        //
        const { info = { Unknown: "An error occurred" } } = error;
        const message = Object.keys(info).map((key) => (
          <Typography variant="body1" key={key}>
            <strong>{key}:</strong>
            {info[key]}
          </Typography>
        ));

        //
        toast.error("There was an error connecting to Okta", {
          position: "bottom-center",
        });
        setErrorMessage(
          (
            <Stack spacing={1} direction="column">
              {message}
            </Stack>
          ) as any
        );
      },

      onSuccess: (data) => {
        const provisionState = data?.state;
        if (provisionState !== undefined) {
          switch (provisionState) {
            case ProvisionState.QUEUED:
              setProvisionPhase(ProvisionPhase.RecreatingApplication);
              break;

            case ProvisionState.RUNNING:
              setProvisionPhase(ProvisionPhase.VerifyingIntegration);
              break;

            case ProvisionState.COMPLETED:
              // celebration on first connection
              if (connectionStore.connections.size < 1) {
                flairActions.startCelebration();
              }

              //
              toast.success("Successfully connected to Okta", {
                position: "bottom-center",
              });

              setRefreshInterval(0);
              setProvisionPhase(ProvisionPhase.Done);
              break;
          }
        }
      },
    }
  );

  //
  const connectToOkta = async () => {
    setProvisionPhase(ProvisionPhase.VerifyingAPIToken);
    setPhase(Phase.Provisioning);

    try {
      await connectionActions.createConnection({
        kind: "okta",
        data: {
          org_url: organization,
          token: apiToken,
        },
      });
    } catch (error) {
      captureSentryException(error, "Failed to create Okta connection");
      setErrorMessage((error as Error)?.message ?? "There was an error");
      setPhase(Phase.Error);
    }
  };

  const retryConnection = () => {
    setErrorMessage(undefined as any);
    setProvisionPhase(ProvisionPhase.VerifyingAPIToken);
    setPhase(Phase.EnterDetails);
  };

  //
  if (connectionStore.fetchPublicKey.state === ActionState.INITIAL) {
    connectionActions.fetchPublicKey({ force: false });
    return <LoaderModal />;
  }

  //
  return (
    <>
      <Breadcrumbs>
        <Breadcrumb
          underline="none"
          color="inherit"
          component={Link}
          to="/environment/sources"
        >
          Environment
        </Breadcrumb>
        <Breadcrumb
          underline="none"
          color="inherit"
          component={Link}
          to="/environment/sources"
        >
          Sources
        </Breadcrumb>
        <span />
      </Breadcrumbs>

      <ServiceConnectionInstructions
        label="Okta Connection"
        forceCollapse={phase > Phase.EnterDetails ? new Date() : undefined}
      >
        <Stack spacing={2}>
          <Typography variant="body1" fontWeight="bold">
            How the ThreatKey for Okta connection works:
          </Typography>
          <Typography variant="body1">
            Connect your ThreatKey account with Okta in a just a few easy steps.
            Once connected, you can see all of your results on the Findings
            page.
          </Typography>
          <Typography variant="body1">
            The process below uses the API key of an administrator to create an
            App with the appropriate permissions. The API key can be deleted
            afterwards.
          </Typography>
          <Typography variant="body1">
            Once connected, ThreatKey analyzes your Okta instance for potential
            misconfigurations and insecure settings. ThreatKey will surface
            recommended settings and configurations to best protect your data
          </Typography>
        </Stack>
      </ServiceConnectionInstructions>

      <Stack spacing={2} direction="column">
        <ConfiguringOktaPhase
          disabled={phase !== Phase.EnterDetails}
          organization={organization}
          apiToken={apiToken}
          mode={mode}
          onChangeOrganization={setOrganization}
          onChangeApiToken={setAPIToken}
          onChangeMode={setMode}
          onNext={() => {
            if (!organization || !apiToken) {
              return;
            }

            connectToOkta();
          }}
        />

        {phase >= Phase.Provisioning ? (
          <>
            <Error
              show={
                provisionPhase === ProvisionPhase.VerifyingAPIToken &&
                phase === Phase.Error
              }
              onTryAgain={retryConnection}
            >
              {errorMessage}
            </Error>

            <VerifyingAPITokenPhase
              disabled={provisionPhase !== ProvisionPhase.VerifyingAPIToken}
              organization={organization}
              progressIcon={provisionPhase === ProvisionPhase.VerifyingAPIToken}
              successIcon={provisionPhase > ProvisionPhase.VerifyingAPIToken}
              errorIcon={
                provisionPhase === ProvisionPhase.VerifyingAPIToken &&
                phase === Phase.Error
              }
            />

            <Error
              show={
                provisionPhase === ProvisionPhase.RecreatingApplication &&
                phase === Phase.Error
              }
              onTryAgain={retryConnection}
            >
              {errorMessage}
            </Error>

            <RecreatingApplicationPhase
              disabled={provisionPhase !== ProvisionPhase.RecreatingApplication}
              progressIcon={
                provisionPhase === ProvisionPhase.RecreatingApplication
              }
              successIcon={
                provisionPhase > ProvisionPhase.RecreatingApplication
              }
              errorIcon={
                provisionPhase === ProvisionPhase.RecreatingApplication &&
                phase === Phase.Error
              }
            />

            <Error
              show={
                provisionPhase === ProvisionPhase.VerifyingIntegration &&
                phase === Phase.Error
              }
              onTryAgain={retryConnection}
            >
              {errorMessage}
            </Error>

            <VerifyingIntegrationPhase
              disabled={provisionPhase !== ProvisionPhase.VerifyingIntegration}
              progressIcon={
                provisionPhase === ProvisionPhase.VerifyingIntegration
              }
              successIcon={provisionPhase > ProvisionPhase.VerifyingIntegration}
              errorIcon={
                provisionPhase === ProvisionPhase.VerifyingIntegration &&
                phase === Phase.Error
              }
            />

            <CompletedPhase
              disabled={provisionPhase !== ProvisionPhase.Done}
              successIcon={provisionPhase === ProvisionPhase.Done}
              onClickFn={() => {
                connectionActions.loadConnections({ reload: true });
              }}
            />
          </>
        ) : null}
      </Stack>
    </>
  );
});

export default OktaConnectSetupPage;

// -----------------------------------------------------------------------------

interface ProvisionStateResponse {
  state: ProvisionState;
  secret_state?: SecretState;
  secret_details?: string;
  details: any;
}
